"use client";

import { useEffect, useMemo, useState } from "react";

import ChevronRightIcon from "@repo/ui/assets/icons/chevron-right-icon.svg";
import { JetSmartButton } from "@repo/ui/components/common/forms/JetSmartButton";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
} from "@repo/ui/components/ui/dialog";
import {
  PAYMENT_APPROVED_GRACE_MS,
  PAYMENT_APPROVED_STORAGE_KEY,
  withTabSession,
} from "@repo/ui/constants/payment";
import { getTabSessionId } from "@repo/ui/lib/tabSessionId";
import { useLocalizedRouter } from "@repo/ui/navigation";
import type { ManageMyBookingModalDTO } from "@repo/ui/types/itinerary";
import { PaymentStatus } from "@repo/ui/types/manageBooking";

import { useAppDispatch } from "@repo/store/hooks";
import {
  setIsPaymentRetry,
  setPaymentMethod,
  setPNR,
  setShouldShowPaymentDetails,
  setShowFinalizationModal,
  setYunoPaymentStatus,
  useManageBooking,
  useRecordLocator,
  useShowFinalizationModal,
  useYunoPaymentStatus,
} from "@repo/store/slices";

type PaymentRetryModalProps = Readonly<{
  mmbModalContent?: ManageMyBookingModalDTO[] | null;
  /** In-app payment route. Locale and zone are added by useLocalizedRouter where the app has them. */
  paymentPath?: string;
}>;

export default function PaymentRetryModal({
  mmbModalContent,
  paymentPath = "/payment",
}: PaymentRetryModalProps) {
  const router = useLocalizedRouter();
  const dispatch = useAppDispatch();

  const booking = useManageBooking();
  const recordLocator = useRecordLocator();
  const isOpenFinalizationModal = useShowFinalizationModal();
  const yunoPaymentStatus = useYunoPaymentStatus();

  const payments = booking?.payments?.[booking.payments.length - 1];
  const paymentStatus = payments?.paymentStatus?.toLowerCase();

  // Check if payment is fully paid and balance is 0
  const isFullyPaid = booking?.paymentStatus === PaymentStatus.PaidInFull;
  const isNotFullyPaid = !isFullyPaid;
  const balanceDue = booking?.breakdown?.balanceDue ?? 0;
  const hasBalanceDue = balanceDue > 0;

  const isSessionDeclined = useMemo(() => {
    try {
      const item = sessionStorage.getItem("jetsmart_payment_declined");
      if (!item) return false;
      const data = JSON.parse(item);
      return (
        data.recordLocator === recordLocator &&
        Date.now() - new Date(data.timestamp).getTime() < PAYMENT_APPROVED_GRACE_MS
      );
    } catch {
      return false;
    }
  }, [recordLocator]);

  // Check if payment status is declined (from booking, Redux, or post-commit session storage)
  const isPaymentDeclined =
    paymentStatus === "declined" ||
    yunoPaymentStatus?.toLowerCase() === "declined" ||
    isSessionDeclined;

  // CRITICAL: Check for recent successful payment from localStorage.
  // PostCommit writes the approved key namespaced by tab session ID, so we must read
  // the same namespaced key here — using the bare key was always returning null (mismatch).
  const hasRecentSuccessfulPayment = useMemo(() => {
    try {
      // Use the tab-namespaced key that matches what PostCommitContent writes.
      const tabSessionId = getTabSessionId();
      const approvedKey = withTabSession(PAYMENT_APPROVED_STORAGE_KEY, tabSessionId);
      const approved = localStorage.getItem(approvedKey);
      if (approved) {
        const approvedData = JSON.parse(approved);
        const approvedTimestamp = new Date(approvedData.timestamp).getTime();
        if (
          approvedData.recordLocator === recordLocator &&
          Date.now() - approvedTimestamp < PAYMENT_APPROVED_GRACE_MS
        ) {
          return true;
        }
      }

      // Fallback: jetsmart_payment_success is written by ProcessingModal before the
      // PostCommit redirect and is still present if the user somehow lands on the
      // itinerary before PostCommit has run (edge-case). PostCommit deletes it on exit,
      // so this only fires in the very narrow window before PostCommit cleans up.
      const paymentSuccess = localStorage.getItem("jetsmart_payment_success");
      if (!paymentSuccess) return false;

      const data = JSON.parse(paymentSuccess);
      const timestamp = new Date(data.timestamp).getTime();
      const fiveMinutes = 5 * 60 * 1000;

      return (
        data.showModal &&
        data.recordLocator === recordLocator &&
        Date.now() - timestamp < fiveMinutes
      );
    } catch {
      return false;
    }
  }, [recordLocator]);

  // CRITICAL: Follow the same pattern as usePaymentValidation hook
  // Show modal ONLY when:
  // 1. Booking data exists
  // 2. Finalization modal is not open
  // 3. No recent successful payment (prevents showing during data update delay)
  // 4. (Payment is not fully paid OR has balance due) - matches usePaymentValidation pattern
  // 5. AND payment status is declined
  // This ensures old declined payments won't show modal when payment is successful
  const isOpen = useMemo(() => {
    return Boolean(
      booking &&
      !isOpenFinalizationModal &&
      !hasRecentSuccessfulPayment && // Block during grace period after successful payment
      (isNotFullyPaid || hasBalanceDue || isSessionDeclined) &&
      isPaymentDeclined,
    );
  }, [
    booking,
    isOpenFinalizationModal,
    hasRecentSuccessfulPayment,
    isNotFullyPaid,
    hasBalanceDue,
    isSessionDeclined,
    isPaymentDeclined,
  ]);

  const [open, setOpen] = useState(isOpen);

  const retryModalContent =
    mmbModalContent?.find((item) => item.codename === "booking_failed") ?? null;

  useEffect(() => {
    setOpen(isOpen);
    return () => setOpen(false);
  }, [isOpen]);

  if (!retryModalContent) {
    return null;
  }

  const handleOnclick = () => {
    try {
      sessionStorage.removeItem("jetsmart_payment_declined");
    } catch {
      // ignore
    }
    dispatch(setPNR(recordLocator!));
    dispatch(setIsPaymentRetry(true));
    dispatch(setPaymentMethod(null));
    dispatch(setYunoPaymentStatus(null));
    dispatch(setShouldShowPaymentDetails(false));
    dispatch(setShowFinalizationModal(false));
    router.push(paymentPath);
  };

  return (
    <Dialog open={open}>
      <DialogContent className="data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right max-h-[70vh] w-full gap-0 overflow-y-auto rounded-2xl bg-white p-0 duration-500 [&>button:last-child]:hidden border-0 md:min-w-[604px]">
        <DialogHeader className="bg-[#9e2b2f] py-4 px-6 flex flex-row items-center justify-between">
          <strong
            className="text-[24px] text-white font-encode"
            dangerouslySetInnerHTML={{
              __html: retryModalContent.heading ?? "",
            }}
          />

          <DialogClose asChild>
            <button
              className="cursor-pointer"
              aria-label="Close modal"
              onClick={() => setOpen(false)}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M23.6134 0.39053C23.0927 -0.130177 22.2485 -0.130177 21.7278 0.39053L12.0019 10.1164L2.27614 0.39053C1.75545 -0.130177 0.911223 -0.130177 0.39053 0.39053C-0.130177 0.911223 -0.130177 1.75545 0.39053 2.27614L10.1163 12.002L0.390557 21.7277C-0.13015 22.2485 -0.13015 23.0926 0.390557 23.6135C0.91125 24.1341 1.75548 24.1341 2.27617 23.6135L12.0019 13.8876L21.7278 23.6135C22.2485 24.1341 23.0927 24.1341 23.6134 23.6135C24.1341 23.0926 24.1341 22.2485 23.6134 21.7278L13.8875 12.002L23.6134 2.27614C24.1341 1.75545 24.1341 0.911223 23.6134 0.39053Z"
                  fill="white"
                />
              </svg>
            </button>
          </DialogClose>

          <DialogDescription className="sr-only hidden">
            {retryModalContent.heading}
          </DialogDescription>
        </DialogHeader>

        <div
          className="
    pl-4                      
    text-[14px] leading-6 text-brand-black-90

    [&>p]:font-bold
    [&>p]:text-brand-black-100
    [&>p]:mb-2

    [&>ol]:list-decimal
    [&>ol]:list-inside
    [&>ol]:space-y-1
  "
          dangerouslySetInnerHTML={{
            __html: retryModalContent.message ?? "",
          }}
        />

        <div className="px-9 pb-8 flex justify-center">
          <JetSmartButton
            bgColor="bg-brand-red"
            hoverColor="hover:bg-brand-blue"
            textColor="text-white"
            borderColor=""
            iconSrc={ChevronRightIcon}
            iconAlt="Round Arrow"
            iconClassName="w-5 h-5"
            className="h-[2.65rem] w-max md:min-w-[259px]"
            onClick={handleOnclick}
            data-testid="retry-payment-button"
          >
            {retryModalContent.buttonText}
          </JetSmartButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
